import React, { useState, useEffect, useCallback } from "react";
import { FaChartBar, FaChartLine, FaUsers, FaUserTag, FaEye, FaUserFriends, FaUserClock, FaFileAlt, FaTrophy, FaCalculator } from "react-icons/fa";
import { useAnalytics } from "../../hooks/useAnalytics";

// Constants
import { DEFAULT_DASHBOARD_WIDGETS, GRAPH_OPTIONS, TABLE_OPTIONS } from "./constants";

// Components
import AnalyticsHeader from "./components/AnalyticsHeader";
import AnalyticsFilters from "./components/AnalyticsFilters";
import KpiCards from "./components/KpiCards";
import CustomLayouts from "./components/CustomLayouts";
import CustomizerDrawer from "./components/CustomizerDrawer";

const Analytics = () => {
  // Date range state
  const today = new Date().toISOString().split("T")[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo);
  const [dateTo, setDateTo] = useState(today);
  const [selectedReport, setSelectedReport] = useState("");
  const [selectedPersona, setSelectedPersona] = useState("");
  const [selectedUser, setSelectedUser] = useState("");

  // Analytics hook
  const { loading, kpis, filters, charts, tables, fetchAnalytics, dashboardConfig, fetchDashboardConfig, saveDashboardConfig } = useAnalytics();

  // Customizer state
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [customizerWidth, setCustomizerWidth] = useState("wide");
  const [draftWidgets, setDraftWidgets] = useState(DEFAULT_DASHBOARD_WIDGETS.map((widget, index) => ({ ...widget, order: index + 1 })));
  const [savingConfig, setSavingConfig] = useState(false);
  const [configMessage, setConfigMessage] = useState("");
  const [configLoaded, setConfigLoaded] = useState(false);
  const [showKpiPicker, setShowKpiPicker] = useState(false);
  const [showLayoutPicker, setShowLayoutPicker] = useState(false);
  const [customLayouts, setCustomLayouts] = useState([]);
  const [activeSlotPicker, setActiveSlotPicker] = useState(null);
  const [activeKpiSlot, setActiveKpiSlot] = useState(null);
  const [selectedKpiSlots, setSelectedKpiSlots] = useState([null, null, null, null]);

  // Computed values
  const usedGraphIds = customLayouts.flatMap((layout) => layout.slots?.filter((slot) => slot?.type === "graph").map((slot) => slot.id) || []);
  const usedTableIds = customLayouts.flatMap((layout) => layout.slots?.filter((slot) => slot?.type === "table").map((slot) => slot.id) || []);
  const usedKpiIds = selectedKpiSlots.filter(Boolean);
  const hasCustomContent = usedKpiIds.length > 0 || customLayouts.length > 0;
  const showDefaultSections = configLoaded && !hasCustomContent;

  // Layout actions
  const addCustomLayout = (columns) => {
    setCustomLayouts((prev) => [...prev, { id: Date.now(), columns, slots: Array(columns).fill(null) }]);
    setShowLayoutPicker(false);
  };

  const removeCustomLayout = (id) => setCustomLayouts((prev) => prev.filter((layout) => layout.id !== id));

  const selectSlotItem = (layoutId, slotIndex, itemId, itemType) => {
    // Get default title from constants
    const defaultTitle = itemType === "graph"
      ? GRAPH_OPTIONS.find((g) => g.id === itemId)?.label
      : TABLE_OPTIONS.find((t) => t.id === itemId)?.label;
    
    setCustomLayouts((prev) =>
      prev.map((layout) => {
        if (layout.id !== layoutId) return layout;
        const newSlots = [...layout.slots];
        newSlots[slotIndex] = { id: itemId, type: itemType, title: defaultTitle };
        return { ...layout, slots: newSlots };
      })
    );
    setActiveSlotPicker(null);
  };

  const updateSlotTitle = (layoutId, slotIndex, newTitle) => {
    setCustomLayouts((prev) =>
      prev.map((layout) => {
        if (layout.id !== layoutId) return layout;
        const newSlots = [...layout.slots];
        if (newSlots[slotIndex]) {
          newSlots[slotIndex] = { ...newSlots[slotIndex], title: newTitle };
        }
        return { ...layout, slots: newSlots };
      })
    );
  };

  const clearSlotItem = (layoutId, slotIndex) => {
    setCustomLayouts((prev) =>
      prev.map((layout) => {
        if (layout.id !== layoutId) return layout;
        const newSlots = [...layout.slots];
        newSlots[slotIndex] = null;
        return { ...layout, slots: newSlots };
      })
    );
  };

  // KPI slot actions
  const selectKpiForSlot = (kpiId) => {
    if (activeKpiSlot === null) return;
    setSelectedKpiSlots((prev) => {
      const newSlots = [...prev];
      newSlots[activeKpiSlot] = kpiId;
      return newSlots;
    });
    setShowKpiPicker(false);
    setActiveKpiSlot(null);
  };

  const clearKpiSlot = (slotIndex) => {
    setSelectedKpiSlots((prev) => {
      const newSlots = [...prev];
      newSlots[slotIndex] = null;
      return newSlots;
    });
  };

  // Filter actions
  const applyFilters = useCallback(() => {
    fetchAnalytics({
      startDate: dateFrom,
      endDate: dateTo,
      reportId: selectedReport || undefined,
      personaId: selectedPersona || undefined,
      userEmail: selectedUser || undefined,
    });
  }, [dateFrom, dateTo, selectedReport, selectedPersona, selectedUser, fetchAnalytics]);

  const resetFilters = useCallback(() => {
    setDateFrom(thirtyDaysAgo);
    setDateTo(today);
    setSelectedReport("");
    setSelectedPersona("");
    setSelectedUser("");
    fetchAnalytics({ startDate: thirtyDaysAgo, endDate: today });
  }, [thirtyDaysAgo, today, fetchAnalytics]);

  // Auto-apply filters when any filter changes
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Load dashboard config
  useEffect(() => {
    fetchDashboardConfig().catch(() => setConfigLoaded(true));
  }, [fetchDashboardConfig]);

  // Process dashboard config
  useEffect(() => {
    if (dashboardConfig?.length) {
      setDraftWidgets(
        DEFAULT_DASHBOARD_WIDGETS.map((defaultWidget, index) => {
          const saved = dashboardConfig.find((widget) => widget.id === defaultWidget.id);
          const settings = { ...defaultWidget.settings, ...(saved?.settings || {}) };
          if (defaultWidget.id === "kpis") {
            settings.layout = settings.layout === "three" ? "three" : "four";
            const visibility = { ...defaultWidget.settings.kpiVisibility, ...(saved?.settings?.kpiVisibility || {}) };
            const selected = Object.keys(visibility).filter((id) => visibility[id]);
            settings.kpiVisibility = Object.fromEntries(Object.keys(visibility).map((id) => [id, visibility[id] && selected.indexOf(id) < 4]));
          }
          return { ...defaultWidget, ...saved, settings, order: saved?.order || index + 1 };
        })
      );
      const customizerConfig = dashboardConfig.find((w) => w.id === "customizer");
      if (customizerConfig?.settings?.selectedKpiSlots) {
        setSelectedKpiSlots(customizerConfig.settings.selectedKpiSlots);
      }
      if (customizerConfig?.settings?.customLayouts) {
        setCustomLayouts(customizerConfig.settings.customLayouts);
      }
      setConfigLoaded(true);
    } else if (dashboardConfig !== null) {
      setConfigLoaded(true);
    }
  }, [dashboardConfig]);

  // Sync selectedKpiSlots and customLayouts to draftWidgets
  useEffect(() => {
    setDraftWidgets((current) => {
      const customizerIdx = current.findIndex((w) => w.id === "customizer");
      if (customizerIdx === -1) {
        return [...current, { id: "customizer", visible: true, settings: { selectedKpiSlots, customLayouts } }];
      }
      return current.map((widget) =>
        widget.id === "customizer" ? { ...widget, settings: { ...widget.settings, selectedKpiSlots, customLayouts } } : widget
      );
    });
  }, [selectedKpiSlots, customLayouts]);

  // Widget helpers
  const isWidgetVisible = (id) => draftWidgets.find((widget) => widget.id === id)?.visible !== false;
  const widgetSetting = (id, key, fallback) => draftWidgets.find((widget) => widget.id === id)?.settings?.[key] ?? fallback;
  const updateWidgetSetting = (id, key, value) =>
    setDraftWidgets((current) => current.map((widget) => (widget.id === id ? { ...widget, settings: { ...widget.settings, [key]: value } } : widget)));

  const kpiVisibility = () => widgetSetting("kpis", "kpiVisibility", {});
  const selectedKpiCount = Object.values(kpiVisibility()).filter(Boolean).length;

  const toggleKpi = (id, checked) => {
    if (checked && selectedKpiCount >= 4) {
      setConfigMessage("You can select a maximum of 4 KPI cards");
      return;
    }
    setConfigMessage("");
    updateWidgetSetting("kpis", "kpiVisibility", { ...kpiVisibility(), [id]: checked });
  };

  // Grid class helpers
  const kpiGridClass = { three: "grid-cols-3", four: "grid-cols-4" }[widgetSetting("kpis", "layout", "four")] || "grid-cols-4";
  const previewKpiGridClass = { "1": "grid-cols-1", "2": "grid-cols-2", "3": "grid-cols-3", "4": "grid-cols-4" }[widgetSetting("kpis", "kpiPerRow", "4")] || "grid-cols-4";
  const kpiDensityClass = widgetSetting("kpis", "density", "comfortable") === "compact" ? "p-3" : "p-4";

  // Save config
  const saveConfig = async () => {
    setSavingConfig(true);
    setConfigMessage("");
    try {
      await saveDashboardConfig(draftWidgets);
      setConfigMessage("Dashboard preferences saved");
    } catch (error) {
      setConfigMessage(error.response?.data?.message || "Unable to save dashboard preferences");
    } finally {
      setSavingConfig(false);
    }
  };

  // Chart data processing
  const lineKeys = charts.byGroup ? Object.keys(charts.byGroup) : [];
  const pieKeys = lineKeys.filter((k) => k !== "MGS Labs Global Old");
  const personaNames = lineKeys;

  const reportNamesUnion = [];
  personaNames.forEach((persona) => {
    charts.byGroup[persona]?.chats?.[0]?.XAxis?.forEach((rname) => {
      if (!reportNamesUnion.includes(rname)) reportNamesUnion.push(rname);
    });
  });

  const reportsByPersonaData = reportNamesUnion.map((reportName) => {
    const row = { reportName };
    personaNames.forEach((persona) => {
      const chat = charts.byGroup[persona]?.chats?.[0];
      const idx = chat?.XAxis?.indexOf(reportName) ?? -1;
      row[persona] = idx !== -1 ? (chat?.YAxis?.[idx] ?? 0) : 0;
    });
    return row;
  });

  const abbreviateReportName = (name) =>
    (name || "")
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0].toUpperCase())
      .join("");

  // KPI value helpers
  const kpiVal = (field) => (typeof kpis[field] === "object" ? kpis[field]?.value : kpis[field]);
  const kpiTrend = (field) => (typeof kpis[field] === "object" ? kpis[field]?.trend : null);

  // KPI cards data
  const kpiCards = [
    { id: "totalReports", label: "Total Reports", value: kpiVal("totalReports"), icon: <FaChartBar size={20} />, trend: kpiTrend("totalReports") || { value: null, up: null, label: "vs last week" } },
    { id: "totalPersonas", label: "Total Personas", value: kpiVal("totalPersonas"), icon: <FaUserTag size={20} />, trend: kpiTrend("totalPersonas") || { value: null, up: null, label: "vs last week" } },
    { id: "totalUsers", label: "Total Users", value: kpiVal("totalUsers"), icon: <FaUserFriends size={20} />, trend: kpiTrend("totalUsers") || { value: null, up: null, label: "vs last week" } },
    { id: "totalActiveUsers", label: "Active Users", value: kpiVal("totalActiveUsers"), icon: <FaUsers size={20} />, trend: kpiTrend("totalActiveUsers") || { value: null, up: null, label: "vs last week" } },
    { id: "totalViews", label: "Total Views", value: kpiVal("totalViews"), icon: <FaEye size={20} />, trend: kpiTrend("totalViews") || { value: null, up: null, label: "vs last week" } },
    { id: "inactiveUsers", label: "Inactive Users", value: kpiVal("inactiveUsers"), icon: <FaUserClock size={20} />, trend: kpiTrend("inactiveUsers") || { value: null, up: null, label: "30+ days" } },
    { id: "unusedReports", label: "Unused Reports", value: kpiVal("unusedReports"), icon: <FaFileAlt size={20} />, trend: kpiTrend("unusedReports") || { value: null, up: null, label: "needs attention" } },
    { id: "topReportViews", label: "Top Report Views", value: kpiVal("topReportViews"), icon: <FaTrophy size={20} />, trend: kpiTrend("topReportViews") || { value: null, up: null, label: "highest report" } },
    { id: "topVisualUses", label: "Top Visual Uses", value: kpiVal("topVisualUses"), icon: <FaChartLine size={20} />, trend: kpiTrend("topVisualUses") || { value: null, up: null, label: "highest visual" } },
    { id: "averageViewsPerUser", label: "Avg Views / User", value: kpiVal("averageViewsPerUser"), icon: <FaCalculator size={20} />, trend: kpiTrend("averageViewsPerUser") || { value: null, up: null, label: "per user" } },
  ];

  return (
    <div className="bg-gray-50 p-6 rounded-xl space-y-6" style={{ accentColor: "var(--app-button-color)" }}>
      {/* Header */}
      <AnalyticsHeader
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onCustomize={() => setShowCustomizer((open) => !open)}
      />

      {/* Customizer Drawer */}
      <CustomizerDrawer
        showCustomizer={showCustomizer}
        setShowCustomizer={setShowCustomizer}
        customizerWidth={customizerWidth}
        setCustomizerWidth={setCustomizerWidth}
        selectedKpiSlots={selectedKpiSlots}
        setActiveKpiSlot={setActiveKpiSlot}
        clearKpiSlot={clearKpiSlot}
        showKpiPicker={showKpiPicker}
        setShowKpiPicker={setShowKpiPicker}
        activeKpiSlot={activeKpiSlot}
        kpiCards={kpiCards}
        usedKpiIds={usedKpiIds}
        selectKpiForSlot={selectKpiForSlot}
        showLayoutPicker={showLayoutPicker}
        setShowLayoutPicker={setShowLayoutPicker}
        addCustomLayout={addCustomLayout}
        customLayouts={customLayouts}
        removeCustomLayout={removeCustomLayout}
        activeSlotPicker={activeSlotPicker}
        setActiveSlotPicker={setActiveSlotPicker}
        selectSlotItem={selectSlotItem}
        clearSlotItem={clearSlotItem}
        updateSlotTitle={updateSlotTitle}
        usedGraphIds={usedGraphIds}
        usedTableIds={usedTableIds}
        previewKpiGridClass={previewKpiGridClass}
        charts={charts}
        tables={tables}
        reportsByPersonaData={reportsByPersonaData}
        personaNames={personaNames}
        pieKeys={pieKeys}
        abbreviateReportName={abbreviateReportName}
        configMessage={configMessage}
        savingConfig={savingConfig}
        saveConfig={saveConfig}
      />

      {/* Filters Bar */}
      <AnalyticsFilters
        filters={filters}
        selectedReport={selectedReport}
        selectedPersona={selectedPersona}
        selectedUser={selectedUser}
        loading={loading}
        onReportChange={setSelectedReport}
        onPersonaChange={setSelectedPersona}
        onUserChange={setSelectedUser}
        onReset={resetFilters}
      />

      {/* KPI Cards */}
      {isWidgetVisible("kpis") && (
        <KpiCards
          loading={loading}
          kpiCards={kpiCards}
          selectedKpiSlots={selectedKpiSlots}
          showDefaultSections={showDefaultSections}
          configLoaded={configLoaded}
          widgetSetting={widgetSetting}
          kpiVisibility={kpiVisibility()}
          kpiDensityClass={kpiDensityClass}
          previewKpiGridClass={previewKpiGridClass}
          kpiGridClass={kpiGridClass}
        />
      )}

      {/* Custom Layouts - Saved Graphs and Tables */}
      <CustomLayouts
        customLayouts={customLayouts}
        loading={loading}
        configLoaded={configLoaded}
        charts={charts}
        tables={tables}
        reportsByPersonaData={reportsByPersonaData}
        personaNames={personaNames}
        pieKeys={pieKeys}
        abbreviateReportName={abbreviateReportName}
      />
    </div>
  );
};

export default Analytics;
